// User administration service layer
// Feature: 004-user-administration
// Updated: 007-cloudflare-migration - migrated to Cloudflare API Gateway
// Updated: 008-better-auth-integration - Added ban/unban support

import * as Sentry from '@sentry/react'
import { listUsers, getUser, getUserBusinesses, getUserMarketplace, getUserJobs, banUser as apiBanUser, unbanUser as apiUnbanUser } from './api/admin'
import { isApiError } from './api/errors'
import type { ApiUser, ApiUserListItem, UserListParams, PaginatedResponse } from './api/types'
import type {
  UserProfile,
  UserProfileComplete,
  LinkedContent,
} from '../types/userAdmin'

/**
 * Transform API user list item to legacy UserProfile format
 * Updated for Better Auth: uses banned status instead of deleted_at
 */
function transformUserListItem(apiUser: ApiUserListItem): UserProfile {
  return {
    id: apiUser.id,
    username: apiUser.display_name ?? undefined,
    email: apiUser.email || undefined,
    // Map banned status - no longer using deleted_at
    status: apiUser.status === 'banned' ? 'banned' : 'active',
    is_verified: apiUser.is_verified,
    ban_reason: apiUser.ban_reason ?? null,
    ban_expires: apiUser.ban_expires ?? null,
    created_at: apiUser.created_at,
  }
}

/**
 * Transform API user to legacy UserProfileComplete format
 * Updated for Better Auth: includes ban fields
 */
function transformUser(apiUser: ApiUser & { roles?: string[]; ban_reason?: string | null; ban_expires?: string | null; is_verified?: boolean; phone?: string | null }): UserProfileComplete {
  // Use roles array if available, otherwise fall back to single role
  const roles = apiUser.roles || [apiUser.role]
  
  return {
    id: apiUser.id,
    username: apiUser.display_name ?? undefined,
    email: apiUser.email || undefined,
    // Map status - banned instead of deleted
    status: apiUser.status === 'banned' ? 'banned' : 'active',
    is_verified: apiUser.is_verified,
    ban_reason: apiUser.ban_reason ?? null,
    ban_expires: apiUser.ban_expires ?? null,
    phone: apiUser.phone ?? null,
    created_at: apiUser.created_at,
    last_login_at: apiUser.last_sign_in_at ?? undefined,
    roles: roles,
    data: {
      username: apiUser.display_name ?? undefined,
      avatar_url: apiUser.avatar_url ?? undefined,
    },
  }
}

/**
 * Fetch list of all users for admin panel
 * @param params - Optional query parameters for filtering/pagination
 * @returns Array of user profiles with minimal fields
 */
export async function getAllUsers(params?: UserListParams) {
  try {
    // Default to 'all' status to include disabled/deleted users for admin view
    const queryParams: UserListParams = {
      ...params,
      status: params?.status ?? 'all',
    }
    const response: PaginatedResponse<ApiUserListItem> = await listUsers(queryParams)
    const profiles = response.data.map(transformUserListItem)
    return { 
      data: profiles, 
      error: null,
      pagination: response.pagination,
    }
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'user-admin', action: 'getAllUsers' },
      })
    }
    return { data: null, error, pagination: null }
  }
}

/**
 * Fetch complete profile for selected user
 * @param userId - UUID of the user
 * @returns Complete user profile with all fields
 */
export async function getUserDetails(userId: string) {
  try {
    const apiUser = await getUser(userId)
    const profile = transformUser(apiUser)
    return { data: profile, error: null }
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'user-admin', action: 'getUserDetails' },
        extra: { userId },
      })
    }
    return { data: null, error }
  }
}

/**
 * Fetch all linked content for a user
 * Fetches businesses, marketplace items, and jobs from admin endpoints
 * @param userId - UUID of the user
 * @returns Combined linked content from all sources
 */
export async function getUserLinkedContent(userId: string): Promise<LinkedContent> {
  try {
    // Fetch all linked content in parallel
    const [businessesData, marketplaceData, jobsData] = await Promise.all([
      getUserBusinesses(userId).catch(() => ({ count: 0, items: [] })),
      getUserMarketplace(userId).catch(() => ({ count: 0, items: [] })),
      getUserJobs(userId).catch(() => ({ count: 0, items: [] })),
    ])

    return {
      businesses: {
        count: businessesData.count,
        items: businessesData.items.map(item => ({
          id: item.id,
          name: item.name || 'Unnamed Business',
          created_at: item.created_at,
          created_by: userId,
        })),
      },
      marketplace: {
        count: marketplaceData.count,
        items: marketplaceData.items.map(item => ({
          id: item.id,
          title: item.title || 'Untitled',
          status: item.status || 'active',
          created_at: item.created_at,
          seller_id: userId,
        })),
      },
      jobs: {
        count: jobsData.count,
        items: jobsData.items.map(item => ({
          id: item.id,
          title: item.title || 'Untitled',
          status: item.status || 'active',
          created_at: item.created_at,
          created_by: userId,
        })),
      },
      alerts: [], // Moderation alerts not yet implemented
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'user-admin', action: 'getUserLinkedContent' },
      extra: { userId },
    })
    
    // Return empty structure on error
    return {
      businesses: { count: 0, items: [] },
      marketplace: { count: 0, items: [] },
      jobs: { count: 0, items: [] },
      alerts: [],
    }
  }
}

/**
 * Disable user account by setting status to 'suspended'
 * @deprecated Use banUserAccount instead
 * @param userId - UUID of the user
 * @returns Updated user profile
 */
export async function disableUserAccount(userId: string) {
  return banUserAccount(userId)
}

/**
 * Enable user account by setting status to 'active'
 * @deprecated Use unbanUserAccount instead
 * @param userId - UUID of the user
 * @returns Updated user profile
 */
export async function enableUserAccount(userId: string) {
  return unbanUserAccount(userId)
}

/**
 * Ban user account with optional reason
 * @param userId - UUID of the user
 * @param reason - Optional reason for the ban
 * @returns Updated user profile
 */
export async function banUserAccount(userId: string, reason?: string) {
  try {
    const apiUser = await apiBanUser(userId, reason)
    const profile = transformUser(apiUser)

    Sentry.addBreadcrumb({
      category: 'user-admin',
      message: 'User account banned',
      level: 'info',
      data: { userId, reason, timestamp: new Date().toISOString() },
    })

    return { data: profile, error: null }
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'user-admin', action: 'banUserAccount' },
        extra: { userId, reason },
      })
    }
    return { data: null, error }
  }
}

/**
 * Unban user account
 * @param userId - UUID of the user
 * @returns Updated user profile
 */
export async function unbanUserAccount(userId: string) {
  try {
    const apiUser = await apiUnbanUser(userId)
    const profile = transformUser(apiUser)

    Sentry.addBreadcrumb({
      category: 'user-admin',
      message: 'User account unbanned',
      level: 'info',
      data: { userId, timestamp: new Date().toISOString() },
    })

    return { data: profile, error: null }
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'user-admin', action: 'unbanUserAccount' },
        extra: { userId },
      })
    }
    return { data: null, error }
  }
}
